import React from 'react';
import { useHistory } from 'react-router-dom';
import { Row, Menu } from 'antd';
import { MenuInfo } from 'rc-menu/lib/interface';
import { ERoutesFranchiseePrivate, ERoutesPrivate, RoutesCirPrefix } from 'common/models/routesModel';
import { hasAccess, hasAnyCirAccess, isFranchisorRole, isSuperAdminRole } from 'common/helpers/permission.helper';
import { isCirEnabled } from 'common/config/env.config';
import { ArrowSquareOut } from 'common/svg';
import { useAppDispatch, useAppSelector } from 'app/store/store.hooks';
import { logout } from 'app/store/reducers/auth.reducer';
import { EPermissionTab } from 'entities/Permission/Permission.models';

export const Header: React.FC = () => {
  // ---> Leave as is. Just to update history and path on-the-go
  useAppSelector((state) => state.router);
  // <---
  const history = useHistory();
  const dispatch = useAppDispatch();
  const { authModelLoading, authUser } = useAppSelector((state) => state.auth);
  const isSuperAdmin = isSuperAdminRole(authUser?.role);
  const isFranchisor = isFranchisorRole(authUser?.role);
  const permissions = authUser?.permissions;
  const path = history.location.pathname;
  const currentPath = path.split(/(^\/[a-zA-Z]*)/)[1];

  // Permission overlay only narrows a franchisor with a profile. superadmin sees everything;
  // a plain franchisor without permissions keeps the legacy "sees all tabs" behaviour.
  const canSee = (tab: EPermissionTab): boolean => {
    if (isSuperAdmin) {
      return true;
    }

    if (isFranchisor && permissions) {
      return hasAccess(permissions, tab);
    }

    return true;
  };

  const onLogout = () => {
    dispatch(logout());
  };

  const redirect = (e: MenuInfo) => {
    history.push(e.key);
  };

  // MBC-84 Phase 2: CIR portal entry for superadmin + franchisors whose profile grants at least
  // one CIR module. NO profile-less fallback here — legacy full admins never had CIR access.
  const visibleCirPortal = isCirEnabled && (isSuperAdmin || hasAnyCirAccess(permissions));

  const redirectToCirPortal = () => {
    // /cir without segments — the CIR sidebar redirects to the first module the user may see.
    history.push(RoutesCirPrefix);
  };

  return (
    <Menu selectedKeys={[currentPath]} mode="horizontal" className="header-franchisor">
      {canSee(EPermissionTab.Users) && (
        <Menu.Item key={ERoutesPrivate.Users} title="Users" onClick={redirect} disabled={authModelLoading}>
          <Row justify="center" align="middle" className="width-full">
            Users
          </Row>
        </Menu.Item>
      )}
      {canSee(EPermissionTab.Menu) && (
        <Menu.Item key={ERoutesPrivate.Menu} title="Menu" onClick={redirect} disabled={authModelLoading}>
          <Row justify="center" align="middle" className="width-full">
            Menu
          </Row>
        </Menu.Item>
      )}
      {canSee(EPermissionTab.Stores) && (
        <Menu.Item key={ERoutesPrivate.Stores} title="Stores" onClick={redirect} disabled={authModelLoading}>
          <Row justify="center" align="middle" className="width-full">
            Stores
          </Row>
        </Menu.Item>
      )}
      {canSee(EPermissionTab.Customers) && (
        <Menu.Item key={ERoutesPrivate.Customers} title="Customers" onClick={redirect} disabled={authModelLoading}>
          <Row justify="center" align="middle" className="width-full">
            Customers
          </Row>
        </Menu.Item>
      )}
      {canSee(EPermissionTab.Feedback) && (
        <Menu.Item key={ERoutesPrivate.Rating} title="Customers feedback" onClick={redirect} disabled={authModelLoading}>
          <Row justify="center" align="middle" className="width-full">
            Customers feedback
          </Row>
        </Menu.Item>
      )}
      {canSee(EPermissionTab.Statistics) && (
        <Menu.Item key={ERoutesPrivate.Statistics} title="Statistics" onClick={redirect} disabled={authModelLoading}>
          <Row justify="center" align="middle" className="width-full">
            Statistics
          </Row>
        </Menu.Item>
      )}
      {canSee(EPermissionTab.Portal) && (
        <Menu.Item
          key={ERoutesFranchiseePrivate.StoreList}
          title="Portal (Revenue)"
          onClick={redirect}
          disabled={authModelLoading}
        >
          <Row justify="center" align="middle" className="width-full">
            Portal (Revenue)
          </Row>
        </Menu.Item>
      )}
      {canSee(EPermissionTab.QR) && (
        <Menu.Item key={ERoutesPrivate.QR} title="QRs" onClick={redirect} disabled={authModelLoading}>
          <Row justify="center" align="middle" className="width-full">
            QRs
          </Row>
        </Menu.Item>
      )}
      {canSee(EPermissionTab.Promos) && (
        <Menu.Item key={ERoutesPrivate.Promo} title="Promo" onClick={redirect} disabled={authModelLoading}>
          <Row justify="center" align="middle" className="width-full">
            Promo
          </Row>
        </Menu.Item>
      )}
      {canSee(EPermissionTab.Announcements) && (
        <Menu.Item key={ERoutesPrivate.Announcements} title="Announcements" onClick={redirect} disabled={authModelLoading}>
          <Row justify="center" align="middle" className="width-full">
            Announcements
          </Row>
        </Menu.Item>
      )}
      {canSee(EPermissionTab.RegionsGroups) && (
        <Menu.Item
          key={ERoutesPrivate.RegionsGroups}
          title="Regions & Groups"
          onClick={redirect}
          disabled={authModelLoading}
        >
          <Row justify="center" align="middle" className="width-full">
            Regions &amp; Groups
          </Row>
        </Menu.Item>
      )}
      {canSee(EPermissionTab.Bitecoins) && (
        <Menu.Item key={ERoutesPrivate.Bitecoin} title="Bitecoin" onClick={redirect} disabled={authModelLoading}>
          <Row justify="center" align="middle" className="width-full">
            Bitecoin
          </Row>
        </Menu.Item>
      )}
      {visibleCirPortal && (
        <Menu.Item key="cir-portal" title="CIR Portal" onClick={redirectToCirPortal} disabled={authModelLoading}>
          <Row justify="center" align="middle" className="width-full">
            CIR Portal
            <ArrowSquareOut className="mb-1 ml-3" />
          </Row>
        </Menu.Item>
      )}
      <Menu.Item
        key={ERoutesPrivate.PasswordAndSecurity}
        title="Password and security"
        onClick={redirect}
        disabled={authModelLoading}
      >
        <Row justify="center" align="middle" className="width-full">
          Password and security
        </Row>
      </Menu.Item>
      <Menu.Item className="header__button_logout" key="logout" title="Log Out" onClick={onLogout} disabled={authModelLoading}>
        Log Out
      </Menu.Item>
    </Menu>
  );
};
